/**
 * conversationsReducer.js
 * Talkroute
 *
 * Created by Vel Genov on 3/14/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
import { REHYDRATE } from "redux-persist";

import {
  FETCH_CONVERSATIONS_BEGIN,
  FETCH_CONVERSATIONS_SUCCESS,
  FETCH_NEW_CONVERSATIONS_SUCCESS,
  FETCH_CONVERSATIONS_FAILURE,
  FETCH_FILTERED_CONVERSATIONS_FAILURE,
  UPDATE_CONVERSATION_READ_STATUS,
  DELETE_CONVERSATION_SUCCESS,
  APPLY_CONVERSATION_FILTER,
  SHOW_CONVERSATION_DETAILS,
  HIDE_CONVERSATION_DETAILS,
  SELECT_CONVERSATION,
  CLOSE_CONVERSATION,
  CREATE_NEW_CONVERSATION,
  UPDATE_CONVERSATION_NUMBER,
  REMOVE_CONVERSATION_NUMBER,
  UPDATE_CONVERSATION_FROM_NUMBER,
  findConversationById,
  countUnreadConversations,
  FETCH_MESSAGES_SUCCESS,
  SEND_MESSAGE_SUCCESS,
  NO_NEW_MESSAGES,
  sortConversations,
  FETCH_ATTACHMENT_SUCCESS,
  FETCH_ATTACHMENT_ERROR,
  DELETE_NEW_CONVERSATION,
  FETCH_MESSAGES_STARTED,
  FETCH_MESSAGES_ENDED,
  SEND_MESSAGE_STARTED,
  SEND_MESSAGE_FAILURE,
  SET_IS_CONVERSATION_SELECTED,
  MULTIPLE_SELECTED_CONVERSATIONS,
  SWITCH_CONVERSATION_MULTIPLE_SELECTION,
  MULTIPLE_SELECTION_TYPES,
  SET_TEMPORARY_CONVERSATION,
  FETCH_CONVERSATIONS_BATCHES_BEGIN,
  FETCH_FILTERED_CONVERSATIONS_BATCHES_BEGIN,
  FETCH_CONVERSATIONS_BATCHES_SUCCESS,
  FETCH_FILTERED_CONVERSATIONS_BATCHES_SUCCESS,
  FETCH_CONVERSATIONS_BATCHES_FAILURE,
  FETCH_FILTERED_CONVERSATIONS_BATCHES_FAILURE,
  FETCH_FILTERED_CONVERSATIONS_BEGIN,
  FETCH_FILTERED_CONVERSATIONS_SUCCESS,
} from "../actions/conversationActions";
import { CLEAR_ERRORS } from "../actions/errorActions";
import dataStateEnum from "../constants/dataStateConstants";
import uiString from "../constants/uiString";
import {
  ConversationData,
  conversationsRehydrator,
  createNewConversation,
  updateConversation,
} from "../services/dataClasses/ConversationData";
import { COUNTRY_CODE } from "../services/dataClasses/TalkroutePhoneNumber";
import validateArray from "../helpers/arrayValidator";
import { CallHistoryData } from "@services/dataClasses";
import { IPagination } from "../models/IPagination";

export interface IConversationsReducer {
  data: ConversationData[];
  isFetchingMessages: boolean,
  conversationPagination: IPagination;
  filterBy: string;
  filteredData: ConversationData[];
  temporaryConversation: ConversationData | null;
  conversationDraft: any;
  unreadCount: number;
  dataState: dataStateEnum;
  conversationDetails: null | string;
  relatedCalls: CallHistoryData[];
  conversationDetailsParent: null | string;
  selectedConversationId?: null | string;
  attachments: any[]; // TODO: model attachments
  isConversationSelected: boolean;
  error: null | string;
  multipleSelectedConversations: string[];
  multipleSelectionMode: boolean;
  totalConversationsOnServer: number;
  runningOffset: number;
}

const initialState: any = {
  data: [],
  isFetchingMessages: false,
  conversationPagination: null,
  filterBy: uiString.ALL_MESSAGES,
  filteredData: [],
  temporaryConversation: null,
  conversationDraft: null,
  unreadCount: 0,
  dataState: dataStateEnum.LOADING,
  conversationDetails: null,
  relatedCalls: [],
  conversationDetailsParent: null,
  selectedConversationId: null,
  attachments: {},
  // It indicates wheter the Text Message Tab is selected or not
  isConversationSelected: false,
  error: null,
  multipleSelectedConversations: [],
  multipleSelectionMode: false,
  totalConversationsOnServer: 0,
  runningOffset: 0,
};

/**
 * A redux function to process conversation actions
 * @param {*} state the redux state
 * @param {*} action passed in action through the conversationActions creator
 */
export default function conversations(state: IConversationsReducer = initialState, action): IConversationsReducer {
  switch (action.type) {
    case REHYDRATE:
      // A redux-persist action. Called immediately after launch
      if (action.payload !== undefined) {
        return {
          ...state,
          data: conversationsRehydrator(action.payload.conversations.data),
          filteredData: conversationsRehydrator(
            action.payload.conversations.data
          ),
          selectedConversationId: null,
          conversationDraft: null,
          conversationPagination:
            action.payload.conversations.conversationPagination,
          unreadCount: action.payload.conversations.unreadCount,
        };
      }

      return {
        ...state,
      };
    case FETCH_CONVERSATIONS_BEGIN:
      // Fetching the conversations has started
      return {
        ...state,
        dataState: dataStateEnum.LOADING,
        error: null,
      };

    case FETCH_FILTERED_CONVERSATIONS_BEGIN:
        // Fetching the FILTERED conversations has started
        return {
          ...state,
          dataState: dataStateEnum.LOADING,
          error: null,
        };

    case FETCH_CONVERSATIONS_BATCHES_BEGIN:
        // Fetching the conversations for batches has started
        return {
          ...state,
          dataState: dataStateEnum.LOADING,
          error: null,
        };

    case FETCH_FILTERED_CONVERSATIONS_BATCHES_BEGIN:
      // Fetching the filtered conversations for batches has started
      return {
        ...state,
        dataState: dataStateEnum.LOADING,
        error: null,
      };

    case SET_IS_CONVERSATION_SELECTED: {
      const { isConversationSelected } = action.payload;
      return {
        ...state,
        isConversationSelected,
      };
    }

    case FETCH_CONVERSATIONS_SUCCESS: {
      // Successfully fetched the conversations
      let { data: newConversations } = action.payload.conversations;
      const { pagination, clearDraft } = action.payload.conversations;
      const { isConversationSelected } = state;
      let { conversationDraft, selectedConversationId } = state;

      if (conversationDraft && clearDraft === true) {
        const newConversationId = conversationDraft.generateConversationId();
        conversationDraft = null;
        selectedConversationId = newConversationId;
      }

      const { data: existingConversations } = state;
      if (validateArray(existingConversations)) {
        // If there are any existing conversations, merge them with the new ones
        newConversations = addAllUniqueConversations({
          conversations: existingConversations,
          newConversations,
        });
      }
      const sortedConversations = sortConversations(newConversations);

      const unreadCount = countUnreadConversations(
        sortedConversations,
        selectedConversationId,
        isConversationSelected
      );

      return {
        ...state,
        dataState: dataStateEnum.READY,
        data: sortedConversations,
        conversationPagination: pagination,
        unreadCount,
        conversationDraft,
        selectedConversationId,
      };
    }

    case FETCH_FILTERED_CONVERSATIONS_SUCCESS: {
      // Successfully fetched the conversations
      let { data: newConversations } = action.payload.conversations;
      const { pagination, clearDraft } = action.payload.conversations;
      const { isConversationSelected } = state;
      let { conversationDraft, selectedConversationId } = state;

      if (conversationDraft && clearDraft === true) {
        const newConversationId = conversationDraft.generateConversationId();
        conversationDraft = null;
        selectedConversationId = newConversationId;
      }

      // const { data: existingConversations } = state;
      // if (validateArray(existingConversations)) {
      //   // If there are any existing conversations, merge them with the new ones
      //   newConversations = addAllUniqueConversations({
      //     conversations: existingConversations,
      //     newConversations,
      //   });
      // }
      const sortedConversations = sortConversations(newConversations);

      const unreadCount = countUnreadConversations(
        sortedConversations,
        selectedConversationId,
        isConversationSelected
      );

      return {
        ...state,
        dataState: dataStateEnum.READY,
        data: sortedConversations,
        conversationPagination: pagination,
        unreadCount,
        conversationDraft,
        selectedConversationId,
        totalConversationsOnServer: pagination.count,
      };
    }

    case FETCH_CONVERSATIONS_BATCHES_SUCCESS: {
      // Successfully fetched the conversations for batches
      let { data: newConversations } = action.payload.conversations;
      const { pagination, clearDraft } = action.payload.conversations;
      const { isConversationSelected } = state;
      let { conversationDraft, selectedConversationId } = state;

      if (conversationDraft && clearDraft === true) {
        const newConversationId = conversationDraft.generateConversationId();
        conversationDraft = null;
        selectedConversationId = newConversationId;
      }

      const { data: existingConversations } = state;
      if (validateArray(existingConversations)) {
        // If there are any existing conversations, merge them with the new ones
        newConversations = addAllUniqueConversations({
          conversations: existingConversations,
          newConversations,
        });
      }
      const sortedConversations = sortConversations(newConversations);

      const unreadCount = countUnreadConversations(
        sortedConversations,
        selectedConversationId,
        isConversationSelected
      );

      return {
        ...state,
        dataState: dataStateEnum.READY,
        data: sortedConversations,
        conversationPagination: pagination,
        filteredData: sortedConversations,
        unreadCount,
        conversationDraft,
        selectedConversationId,
        runningOffset: action.payload.conversations.pagination.offset,
      };
    }

    case FETCH_FILTERED_CONVERSATIONS_BATCHES_SUCCESS: {
      // Successfully fetched the filtered conversations for batches
      let { data: newConversations } = action.payload.conversations;
      const { pagination, clearDraft } = action.payload.conversations;
      const { isConversationSelected } = state;
      let { conversationDraft, selectedConversationId } = state;

      if (conversationDraft && clearDraft === true) {
        const newConversationId = conversationDraft.generateConversationId();
        conversationDraft = null;
        selectedConversationId = newConversationId;
      }

      const { data: existingConversations } = state;
      if (validateArray(existingConversations)) {
        // If there are any existing conversations, merge them with the new ones
        newConversations = addAllUniqueConversations({
          conversations: existingConversations,
          newConversations,
        });
      }
      const sortedConversations = sortConversations(newConversations);

      const unreadCount = countUnreadConversations(
        sortedConversations,
        selectedConversationId,
        isConversationSelected
      );

      return {
        ...state,
        dataState: dataStateEnum.READY,
        data: sortedConversations,
        conversationPagination: pagination,
        filteredData: sortedConversations,
        unreadCount,
        conversationDraft,
        selectedConversationId,
        runningOffset: action.payload.conversations.pagination.offset,
      };
    }

    case FETCH_NEW_CONVERSATIONS_SUCCESS: {
      // Fetched new conversations - these will overwrite the existing ones
      const { data, pagination } = action.payload.conversations;
      const { selectedConversationId, isConversationSelected } = state;
      const sortedConversations = sortConversations(data);

      const unreadCount = countUnreadConversations(
        sortedConversations,
        selectedConversationId,
        isConversationSelected
      );

      return {
        ...state,
        dataState: dataStateEnum.READY,
        data: sortedConversations,
        conversationPagination: pagination,
        filteredData: sortedConversations,
        unreadCount,
        totalConversationsOnServer: action.payload.conversations.pagination.count,
      };
    }

    case FETCH_CONVERSATIONS_FAILURE:
      // Error while fetching conversations
      return {
        ...state,
        dataState: dataStateEnum.ERROR,
        error: action.payload.error,
      };

    case FETCH_FILTERED_CONVERSATIONS_FAILURE:
        // Error while fetching FILTERED conversations
        return {
          ...state,
          dataState: dataStateEnum.ERROR,
          error: action.payload.error,
        };

    case FETCH_CONVERSATIONS_BATCHES_FAILURE:
        // Error while fetching conversations for batches
        return {
          ...state,
          dataState: dataStateEnum.ERROR,
          error: action.payload.error,
        };

    case FETCH_FILTERED_CONVERSATIONS_BATCHES_FAILURE:
        // Error while fetching filtered conversations for batches
        return {
          ...state,
          dataState: dataStateEnum.ERROR,
          error: action.payload.error,
        };

    case DELETE_CONVERSATION_SUCCESS: {
      // Delete a conversation
      const { selectedConversationId, isConversationSelected, temporaryConversation} = state;
      const { conversationId } = action.payload;
      const updatedData = deleteConversationLocal({
        conversations: state.data,
        conversationId,
      });

      const unreadCount = countUnreadConversations(
        updatedData,
        selectedConversationId,
        isConversationSelected
      );
    
      let updatedSelectedConversation = state.selectedConversationId;
      if (updatedSelectedConversation === conversationId) {
        // The currently selected conversation was deleted. Change selected to the next one, or none if no conversations are available
        updatedSelectedConversation = null;

        if (validateArray(updatedData)) {
          const firstConversation = updatedData[0];
          if (firstConversation) {
            const { conversationId: firstConversationId } = firstConversation;

            updatedSelectedConversation = firstConversationId;
          }
        }
      }
      return {
        ...state,
        data: updatedData,
        filteredData: updatedData,
        unreadCount,
        selectedConversationId: updatedSelectedConversation,
        temporaryConversation: temporaryConversation?.conversationId === selectedConversationId ? null : temporaryConversation
      };
    }

    case UPDATE_CONVERSATION_READ_STATUS: {
      // Update the read status of a conversation
      const { conversationId, readStatus } = action.payload;
      const { selectedConversationId, isConversationSelected } = state;
      const updatedData = updateReadStatusLocal({
        conversations: state.data,
        conversationId,
        readStatus,
      });

      const unreadCount = countUnreadConversations(
        updatedData,
        selectedConversationId,
        isConversationSelected
      );

      return {
        ...state,
        data: updatedData,
        filteredData: updatedData,
        unreadCount,
      };
    }

    case APPLY_CONVERSATION_FILTER: {
      // Apply the Conversation filter
      return {
        ...state,
        runningOffset: 0,
        filteredData: action.payload.conversations,
        filterBy: action.payload.filterBy,
      };
    }

    case SHOW_CONVERSATION_DETAILS:
      // Shows details about a conversation
      return {
        ...state,
        conversationDetails: action.payload.conversationId,
        relatedCalls: action.payload.topRelatedCalls,
        conversationDetailsParent: action.payload.parent,
      };

    case HIDE_CONVERSATION_DETAILS:
      // Hides details about a conversation
      return {
        ...state,
        conversationDetails: action.payload.conversation,
        relatedCalls: action.payload.topRelatedCalls,
        conversationDetailsParent: action.payload.parent,
      };

    case SELECT_CONVERSATION:
      // Shows text messages for a conversation
      return {
        ...state,
        selectedConversationId: action.payload.conversationId,
      };

    case MULTIPLE_SELECTED_CONVERSATIONS:
      const conversationId = action.payload.conversationId;
      const {
        multipleSelectedConversations: multipleSelectedConversationsLocal,
        data: conversationsLocal,
      } = state;

      const multipleSelectedConversations = getMultipleSelectedConversations(
        conversationId,
        action.payload.type,
        String(state.selectedConversationId),
        multipleSelectedConversationsLocal,
        conversationsLocal
      );

      return {
        ...state,
        multipleSelectedConversations
      };

    case SWITCH_CONVERSATION_MULTIPLE_SELECTION:
      return {
        ...state,
        multipleSelectionMode: action.payload.multipleSelectionMode,
      };

    case CLOSE_CONVERSATION:
      // Closes the currently open conversation
      return {
        ...state,
        selectedConversationId: null,
        conversationDraft: null,
      };

    case CREATE_NEW_CONVERSATION: {
      // Create a new blank conversation
      const { talkrouteNumber } = action.payload;

      if (state.conversationDraft === null) {
        const newConversation = createNewConversation(talkrouteNumber);

        return {
          ...state,
          conversationDraft: newConversation,
          selectedConversationId: newConversation.conversationId,
        };
      }

      return {
        ...state,
        selectedConversationId: state.conversationDraft.conversationId,
      };
    }

    case DELETE_NEW_CONVERSATION: {
      // Deletes the conversation draft and sets the selectedConversationId to first conversation
      const { data } = state;
      let updatedSelectedConversation: null | string = null;

      if (validateArray(data)) {
        const firstConversation = data[0];
        if (firstConversation) {
          const { conversationId: firstConversationId } = firstConversation;

          updatedSelectedConversation = firstConversationId;
        }
      }

      return {
        ...state,
        conversationDraft: null,
        selectedConversationId: updatedSelectedConversation,
      };
    }

    case SET_TEMPORARY_CONVERSATION: {
      const { conversation, selectConversation } = action.payload;
      if (selectConversation) {
        return {
          ...state,
          temporaryConversation: conversation,
          selectedConversationId: conversation.conversationId,
        };
      } else {
      return {
        ...state,
        temporaryConversation: conversation,
      }};
    }

    case REMOVE_CONVERSATION_NUMBER: {
      // Remove the contact number of an existing conversation draft
      const { conversationDraft } = state;

      if (conversationDraft !== null) {
        const updatedConversation = updateConversation({
          contactNumber: null,
          talkrouteNumber: conversationDraft.talkrouteNumber,
        });

        return {
          ...state,
          conversationDraft: updatedConversation,
        };
      }

      return {
        ...state,
      };
    }

    case UPDATE_CONVERSATION_NUMBER: {
      // Adding a contact number to an existing conversation draft
      const { phoneNumber } = action.payload;
      const { conversationDraft } = state;

      if (conversationDraft !== null) {
        const updatedPhoneNumber =
          phoneNumber.charAt(0) === COUNTRY_CODE || phoneNumber.includes("+1")
            ? phoneNumber
            : `${COUNTRY_CODE}${phoneNumber}`;
        const updatedConversation = updateConversation({
          contactNumber: updatedPhoneNumber,
          talkrouteNumber: conversationDraft.talkrouteNumber,
        });

        const draftConversationId = updatedConversation.generateConversationId();

        let draftConversationExists = false;
        if (state.data) {
          draftConversationExists = state.data.some(
            (conversation) =>
              conversation.conversationId === draftConversationId
          );
        }

        if (draftConversationExists === true) {
          // The conversation already exists, take the user to it

          return {
            ...state,
            conversationDraft: null,
            selectedConversationId: draftConversationId,
          };
        }

        return {
          ...state,
          conversationDraft: updatedConversation,
        };
      }

      return {
        ...state,
      };
    }

    case UPDATE_CONVERSATION_FROM_NUMBER: {
      // Adding a from number to an existing conversation draft
      const { fromNumber } = action.payload;
      const { conversationDraft } = state;

      if (conversationDraft !== null) {
        const updatedConversation = updateConversation({
          contactNumber: conversationDraft.contactNumber,
          talkrouteNumber: fromNumber,
        });

        return {
          ...state,
          conversationDraft: updatedConversation,
        };
      }

      return {
        ...state,
      };
    }

    case SEND_MESSAGE_STARTED: {
      return {
        ...state,
        isFetchingMessages: true,
      };
    }

    case SEND_MESSAGE_FAILURE: {
      return {
        ...state,
        isFetchingMessages: false,
        error: action.payload.error,
      };
    }

    case SEND_MESSAGE_SUCCESS: {
      // A new message has been sent successfully. Update the local data
      const { message, pagination, conversationId } = action.payload;
      const { selectedConversationId, isConversationSelected, temporaryConversation } = state;
      // incase we are dealing with a conversation for quick reply purposes
      // temporaryConversation is never part of the normal conversation list  
      if (message && temporaryConversation && temporaryConversation.conversationId === selectedConversationId) {
        const copiedTempConversation = {...temporaryConversation};
        // copiedTempConversation?.addMessages({messages: [message], pagination})
        // temporaryConversation.messages
        // copiedTempConversation.messages
        const updatedAttachments = { ...state.attachments };

        const { attachments } = message;
        if (attachments) {
          attachments.forEach((attachment) => {
            updatedAttachments[attachment.id] = attachment;
          });
        }

        const updatedConversation = updateMessagesForConversation({
          messages: [message],
          conversationId,
          conversationsLocal: [temporaryConversation],
          pagination,
        }).pop();

        return {
          ...state,
          temporaryConversation: updatedConversation,
          isFetchingMessages: false,
          attachments: updatedAttachments,
        }

      } else 
      if (message && pagination && conversationId) {
        const { data: conversationsLocal } = state;

        let updatedConversations = conversationsLocal;

        const updatedAttachments = { ...state.attachments };

        const { attachments } = message;
        if (attachments) {
          attachments.forEach((attachment) => {
            updatedAttachments[attachment.id] = attachment;
          });
        }

        updatedConversations = updateMessagesForConversation({
          messages: [message],
          conversationId,
          conversationsLocal,
          pagination,
        });

        const sortedConversations = sortConversations(updatedConversations);

        const unreadCount = countUnreadConversations(
          sortedConversations,
          selectedConversationId,
          isConversationSelected
        );

        return {
          ...state,
          data: sortedConversations,
          isFetchingMessages: false,
          unreadCount,
          attachments: updatedAttachments,
        };
      }

      return {
        ...state,
        isFetchingMessages: false,
      };
    }

    case FETCH_MESSAGES_STARTED: {
      return {
        ...state,
        isFetchingMessages: true,
      };
    }

    case FETCH_MESSAGES_ENDED: {
      return {
        ...state,
        isFetchingMessages: false,
      };
    }

    case FETCH_MESSAGES_SUCCESS: {
      // New messages have been fetched successfully. Update the local data
      const { messages, pagination, conversationId } = action.payload;
      const {
        selectedConversationId,
        data: conversationsLocal,
        isConversationSelected,
      } = state;

      if (validateArray(messages)) {
        const updatedAttachments = { ...state.attachments };

        messages.forEach((message) => {
          const { attachments } = message;
          if (attachments) {
            attachments.forEach((attachment) => {
              updatedAttachments[attachment.id] = attachment;
            });
          }
        });

        const updatedConversations = updateMessagesForConversation({
          messages,
          conversationId,
          conversationsLocal,
          pagination,
        });

        const sortedConversations = sortConversations(updatedConversations);

        const unreadCount = countUnreadConversations(
          sortedConversations,
          selectedConversationId,
          isConversationSelected
        );

        return {
          ...state,
          data: sortedConversations,
          isFetchingMessages: false,
          filteredData: sortedConversations,
          unreadCount,
          attachments: updatedAttachments,
        };
      }

      return {
        ...state,
        isFetchingMessages: false,
      };
    }

    case NO_NEW_MESSAGES: {
      // Called when an action creator has no new messages to update
      return {
        ...state,
        isFetchingMessages: false,
      };
    }

    case FETCH_ATTACHMENT_SUCCESS: {
      // Successfully fetched attachments
      const { filePath, attachmentId } = action.payload;

      if (filePath && attachmentId) {
        const updatedAttachments = { ...state.attachments };
        const attachmentToUpdate = updatedAttachments[attachmentId];
        if (attachmentToUpdate) {
          attachmentToUpdate.filePath = filePath;
          attachmentToUpdate.hasErrorLoading(false);
          updatedAttachments[attachmentId] = attachmentToUpdate;
          return {
            ...state,
            attachments: updatedAttachments,
          };
        }
      }

      return {
        ...state,
      };
    }

    case FETCH_ATTACHMENT_ERROR: {
      // Error while fetching attachments
      const { attachmentId, error } = action.payload;
      if (attachmentId) {
        const updatedAttachments = { ...state.attachments };
        const attachmentToUpdate = updatedAttachments[attachmentId];
        if (attachmentToUpdate) {
          attachmentToUpdate.hasErrorLoading(true);
          updatedAttachments[attachmentId] = attachmentToUpdate;
          return {
            ...state,
            attachments: updatedAttachments,
          };
        }
      }

      return {
        ...state,
      };
    }

    case CLEAR_ERRORS:
      // Clear any errors
      return {
        ...state,
        error: null,
      };

    default:
      // ALWAYS have a default case in a reducer
      return state;
  }
}

/**
 *
 * @param messages the new messages
 * @param conversationId the ID of the conversation to update
 * @param conversationsLocal a local pointer to the conversations object
 * @param pagination an object that contains data on the total messages available on the server
 */
const updateMessagesForConversation = (props) => {
  const { messages, conversationId, conversationsLocal, pagination } = props;
  if (!validateArray(messages)) {
    return conversationsLocal;
  }

  if (conversationsLocal && conversationId) {
    const foundIndex = findConversationById({
      conversations: conversationsLocal,
      conversationId,
    });

    if (foundIndex !== undefined && foundIndex !== -1) {
      const conversation = conversationsLocal[foundIndex];
      conversation.addMessages({ messages, pagination });

      const updatedConversations = [...conversationsLocal];
      updatedConversations[foundIndex] = conversation;

      return updatedConversations;
    }
  }
  return conversationsLocal;
};

/**
 * Finds the index of a conversation and performs error checking. Returns -1 if not found
 *
 * @param {*} conversations the list of conversations to look in
 * @param {*} newConversation the convesation to look for
 */
const findConversationIndex = (props) => {
  const { conversations: conversationsLocal, newConversation } = props;

  const { conversationId } = newConversation;

  if (conversationsLocal && conversationId) {
    const foundIndex = findConversationById({
      conversations: conversationsLocal,
      conversationId,
    });

    return foundIndex;
  }

  return -1;
};

/**
 * Merge a new list of conversations with the existing one
 *
 * @param {*} conversations the existing list of conversations
 * @param {*} newConversations the new conversations
 */
const addAllUniqueConversations = (props) => {
  const { conversations: conversationsLocal, newConversations } = props;

  if (validateArray(newConversations)) {
    const uniqueConversations: any[] = [];
    newConversations.forEach((newConversation) => {
      const conversationIndex = findConversationIndex({
        conversations: conversationsLocal,
        newConversation,
      });
      if (conversationIndex === -1) {
        // This is a unique conversation
        uniqueConversations.push(newConversation);
      } else {
        // This is an existing conversation with index conversationIndex
        const existingConversation = conversationsLocal[conversationIndex];
        if (existingConversation) {
          existingConversation.mergeWithUpdatedConversation(newConversation);
        }
      }
    });

    const updatedConversations = [
      ...conversationsLocal,
      ...uniqueConversations,
    ];
    return updatedConversations;
  }

  return conversationsLocal;
};

/**
 * Delete a conversation locally
 *
 * @param conversations the conversations to delete from
 * @param conversationId the id of the conversation to be deleted
 * @returns the updated array of conversations
 */
const deleteConversationLocal = (props) => {
  const { conversations: conversationsLocal, conversationId } = props;
  if (conversationsLocal && conversationId) {
    const foundIndex = findConversationById({
      conversations: conversationsLocal,
      conversationId,
    });
    // Remove 1 element from foundIndex, and create a new array
    const updatedConversations = [...conversationsLocal];
    updatedConversations.splice(foundIndex, 1);
    return updatedConversations;
  }
  return conversationsLocal;
};

/**
 * Update the read status of the voicemails local instance
 *
 * @param voicemails the array of voicemails to search in
 * @param voicemailId the voicemail to update
 * @param readStatus the updated read status
 * @returns an updated copy of the voicemails
 */
export const updateReadStatusLocal = (props) => {
  const {
    conversations: conversationsLocal,
    conversationId,
    readStatus,
  } = props;

  if (conversationsLocal && conversationId) {
    const foundIndex = findConversationById({
      conversations: conversationsLocal,
      conversationId,
    });
    if (foundIndex !== undefined) {
      const conversation = conversationsLocal[foundIndex];
      if (conversation !== undefined) {
        conversation.lastMessage.isRead = readStatus;
        const updatedConversations = [...conversationsLocal];
        updatedConversations[foundIndex] = conversation;
        return updatedConversations;
      }
    }
  }
  return conversationsLocal;
};

/**
 * Get current multiple selected conversation and the newly selected ones
 * Maintain state.multipleSelectedConversations
 */
const getMultipleSelectedConversations = (
  conversationId: string,
  type: string,
  selectedConversationId: string,
  multipleSelectedConversationsLocal: string[],
  conversationsLocal: any[]
) => {
  let updatedMultipleSelectedConversations;
  if (type === MULTIPLE_SELECTION_TYPES.RANGE) {
    if (selectedConversationId !== conversationId) {
      const startIndex = conversationsLocal.findIndex(
        (c) => c.conversationId === selectedConversationId
      );
      const endIndex = conversationsLocal.findIndex(
        (c) => c.conversationId === conversationId
      );

      const newMultipleSelectedConversations = conversationsLocal
        .slice(
          Math.min(startIndex, endIndex),
          Math.max(startIndex, endIndex) + 1
        )
        .map(
          (c) =>
            !multipleSelectedConversationsLocal?.includes(c.conversationId) &&
            c.conversationId
        );

      updatedMultipleSelectedConversations = [
        ...multipleSelectedConversationsLocal,
        ...newMultipleSelectedConversations,
      ];
    }
  } else if (type === MULTIPLE_SELECTION_TYPES.SINGLE) {
    updatedMultipleSelectedConversations = multipleSelectedConversationsLocal?.includes(
      conversationId
    )
      ? multipleSelectedConversationsLocal.filter(
          (c: string) => c != conversationId
        )
      : [...multipleSelectedConversationsLocal, conversationId];
  } else if (type === MULTIPLE_SELECTION_TYPES.ALL) {
    updatedMultipleSelectedConversations = conversationsLocal.map(
      (c) => c.conversationId
    );
  } else if (type === MULTIPLE_SELECTION_TYPES.NONE) {
    updatedMultipleSelectedConversations = [];
  }
  return updatedMultipleSelectedConversations;
};
